import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Receipt,
  UtensilsCrossed,
  Users,
  X,
} from "lucide-react"
import type { HotelOption } from "@/lib/api/types"
import { formatPriceDelta } from "@/lib/booking"
import { Button } from "@/components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
  type CarouselApi,
} from "@/components/ui/carousel"
import { Dialog, DialogContent } from "@/components/ui/dialog"

export interface MealAddOnLabel {
  name: string
  included: boolean
}

interface HotelCardProps {
  hotel: HotelOption
  visuallySelected: boolean
  isIncludedInPackage: boolean
  currency: string
  selectedPriceDiffPerPerson: number
  mealAddOnLabels: MealAddOnLabel[]
  onSelect: () => void
  onOpenBookingRequest?: () => void
}

export function HotelCard({
  hotel,
  visuallySelected,
  isIncludedInPackage,
  currency,
  selectedPriceDiffPerPerson,
  mealAddOnLabels,
  onSelect,
  onOpenBookingRequest,
}: HotelCardProps) {
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const images = useMemo(
    () => (hotel.images.length > 0 ? hotel.images : []),
    [hotel.images]
  )

  let priceLabel: string
  let showPrice: boolean

  if (hotel.soldOut) {
    priceLabel = ""
    showPrice = false
  } else if (isIncludedInPackage) {
    priceLabel = "Included in package"
    showPrice = true
  } else if (visuallySelected) {
    showPrice = false
    priceLabel = ""
  } else {
    const diff =
      selectedPriceDiffPerPerson === 0
        ? hotel.priceDiffPerPerson
        : hotel.priceDiffPerPerson - selectedPriceDiffPerPerson
    priceLabel = formatPriceDelta(diff, currency, true)
    showPrice = true
  }

  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-[18px] border transition-all @min-[48rem]:flex-row ${
        visuallySelected
          ? "border-primary/60"
          : "border-border/90 hover:border-border"
      }`}
    >
      <div className="relative h-52 w-full shrink-0 overflow-hidden @min-[48rem]:h-auto @min-[48rem]:w-[320px]">
        {images.length > 0 ? (
          <HotelGallery
            images={images}
            hotelName={hotel.name}
            currentIndex={imageIndex}
            onIndexChange={setImageIndex}
            onOpenFullscreen={() => setGalleryOpen(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-secondary text-center text-sm text-muted-foreground">
            Images will be available soon
          </div>
        )}
      </div>

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent
          /*
           * The base DialogContent is a centred panel: max-h-[90dvh] with
           * overflow-y-auto and a translate that centres it. Left in place,
           * the gallery became a 90%-tall box that scrolled inside itself —
           * which is what carried the arrows up and down the screen and let
           * the page show through underneath. Every one of those has to be
           * turned off by name; a plain overflow-hidden does not beat
           * overflow-y-auto, they are different properties.
           */
          className="fixed top-0 right-0 bottom-0 left-0 z-50 h-[100dvh] max-h-none w-screen max-w-none translate-x-0 translate-y-0 overflow-y-hidden overflow-x-hidden rounded-none border-0 bg-black/90 p-0 @min-[40rem]:top-0 @min-[40rem]:left-0 @min-[40rem]:h-[100dvh] @min-[40rem]:max-h-none @min-[40rem]:w-screen @min-[40rem]:max-w-none @min-[40rem]:translate-x-0 @min-[40rem]:translate-y-0 @min-[40rem]:rounded-none @min-[40rem]:p-0 @min-[64rem]:bg-black/80"
          showCloseButton={false}
        >
          <div
            className="absolute inset-0"
            onClick={() => setGalleryOpen(false)}
          />
          <div className="relative flex h-full min-h-0 flex-col p-2 @min-[40rem]:p-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setGalleryOpen(false)}
              className="absolute top-3 right-3 z-10 size-10 rounded-full bg-black/40 text-white/70 backdrop-blur-sm hover:bg-black/60 hover:text-white"
            >
              <X className="size-5" />
            </Button>

            <HotelFullscreenGallery
              images={images}
              hotelId={hotel.hotelId}
              hotelName={hotel.name}
              startIndex={imageIndex}
              onIndexChange={setImageIndex}
            />
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-1 flex-col justify-between gap-4 p-5 @min-[48rem]:p-6">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-[22px] leading-none font-bold text-foreground">
              {hotel.name}
            </h3>

            <div className="mt-2 flex gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <span
                  key={index}
                  className={`text-base ${
                    index < (hotel.stars ?? 0)
                      ? "text-amber-400"
                      : "text-foreground/20"
                  }`}
                >
                  ★
                </span>
              ))}
            </div>
            {hotel.googleReviewsUrl && (
              <a
                href={hotel.googleReviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-amber-400 transition-colors hover:text-amber-400/80"
              >
                Google Reviews
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>

          <p className="text-[15px] leading-6 text-foreground/85">
            {hotel.description || "Comfortable stay close to the event venue."}
          </p>

          <div className="flex flex-wrap gap-2">
            <MetaPill>{hotel.roomCapacity}-person room</MetaPill>
            {mealAddOnLabels.map((meal) => (
              <span
                key={meal.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-green-700/30 bg-green-600/10 px-3 py-1.5 text-xs font-medium text-green-700 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-400"
              >
                <UtensilsCrossed className="size-3.5" />
                {meal.name} {meal.included ? "included" : "available"}
              </span>
            ))}
            {hotel.shared && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 px-3 py-1.5 text-xs font-medium text-blue-500"
              >
                Shared room
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[15px] font-medium text-foreground">
              <MapPin className="size-4 text-muted-foreground" />
              <span>
                {hotel.distanceFromVenue || "Venue distance on request"}
              </span>
            </div>

            {/*
              Said plainly at the point of choosing, not only at checkout: a
              charge the traveller only hears about on arrival is the kind of
              surprise that makes the whole price look dishonest.
            */}
            {hotel.localFee && (
              <div className="flex items-start gap-2 text-[13px] text-muted-foreground">
                <Receipt className="mt-0.5 size-4 shrink-0" />
                <span>
                  {hotel.localFee}
                  <span className="text-muted-foreground/70">
                    {" "}
                    — paid at the hotel, not included in the package price
                  </span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-2 text-[15px] text-blue-500">
              <Users className="size-4 text-blue-500" />
              {hotel.soldOut ? (
                <Button
                  type="button"
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenBookingRequest?.()
                  }}
                  className="h-auto p-0 text-[13px] text-blue-500 underline decoration-blue-500/40 underline-offset-2 hover:text-blue-500/80"
                >
                  Sold out — interested? Tell us
                </Button>
              ) : (
                <span>Only {hotel.availableRooms} rooms left</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end justify-between gap-4 @min-[40rem]:flex-row @min-[40rem]:items-end">
          <div className="min-h-11 flex-1 @min-[40rem]:block hidden" />

          <div className="flex w-full flex-col items-end gap-3 @min-[40rem]:w-auto">
            {showPrice && (
              <div className="text-end">
                <span
                  className={`leading-none ${
                    hotel.soldOut
                      ? "text-[20px] font-bold text-muted-foreground"
                      : isIncludedInPackage
                        ? "text-[15px] font-medium text-foreground"
                        : "text-[26px] font-bold text-primary @min-[48rem]:text-[32px]"
                  }`}
                >
                  {priceLabel}
                </span>
              </div>
            )}

            <Button
              onClick={hotel.soldOut ? onOpenBookingRequest : onSelect}
              variant={hotel.soldOut ? "destructive" : visuallySelected ? "outline" : "default"}
              size="xl"
              type="button"
              className="w-full @min-[40rem]:w-auto"
            >
              {visuallySelected && <Check className="mr-2 size-5" />}
              {hotel.soldOut
                ? "Sold Out"
                : visuallySelected
                  ? "Selected"
                  : "Select Hotel"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface HotelGalleryProps {
  images: string[]
  hotelName: string | null
  currentIndex: number
  onIndexChange: (index: number) => void
  onOpenFullscreen: () => void
}

/**
 * Inline gallery inside the hotel card. Uses shadcn Carousel (Embla) with
 * looping and swipe gestures. Tapping a slide opens the full-screen viewer.
 */
function HotelGallery({
  images,
  hotelName,
  currentIndex,
  onIndexChange,
  onOpenFullscreen,
}: HotelGalleryProps) {
  const [api, setApi] = useState<CarouselApi>()

  // Sync external index → carousel
  useEffect(() => {
    if (!api) return
    if (api.selectedScrollSnap() !== currentIndex) {
      api.scrollTo(currentIndex)
    }
  }, [api, currentIndex])

  // Sync carousel → external index
  useEffect(() => {
    if (!api) return
    const handleSelect = () => onIndexChange(api.selectedScrollSnap())
    api.on("select", handleSelect)
    return () => {
      api.off("select", handleSelect)
    }
  }, [api, onIndexChange])

  return (
    <Carousel
      setApi={setApi}
      opts={{ loop: images.length > 1 }}
      className="h-full [&_[data-slot=carousel-content]]:h-full"
    >
      <CarouselContent className="ml-0 h-full">
        {images.map((src, idx) => (
          <CarouselItem
            key={`${hotelName ?? "hotel"}-${idx}`}
            className="relative h-full pl-0"
          >
            {/*
              The photo is taken out of the flow on purpose. Left in it, a
              lazily loaded image decides the card's height the moment it
              arrives: a 600x1400 photo turned a 349px card into a 752px one
              and carried the arrows ~200px down with it, at whatever moment
              the browser got round to loading it. Out of the flow, the card
              is sized by its text and every photo is cropped into that box.
            */}
            <Button
              type="button"
              variant="ghost"
              onClick={onOpenFullscreen}
              className="absolute inset-0 h-full w-full cursor-pointer rounded-none p-0"
              aria-label={`Open ${hotelName ?? "hotel"} gallery`}
            >
              <img
                src={src}
                alt={`${hotelName ?? "Hotel"} photo ${idx + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </Button>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-r from-transparent via-black/10 to-black/45" />
          </CarouselItem>
        ))}
      </CarouselContent>

      {images.length > 1 && (
        <>
          <GalleryNavButton direction="prev" />
          <GalleryNavButton direction="next" />
        </>
      )}
    </Carousel>
  )
}

/**
 * Custom nav button that overlays the carousel image. We don't use the
 * default `CarouselPrevious`/`CarouselNext` because they're positioned
 * outside the carousel container.
 */
function GalleryNavButton({ direction }: { direction: "prev" | "next" }) {
  const { scrollPrev, scrollNext } = useCarousel()
  const isPrev = direction === "prev"

  return (
    // The centring lives on this wrapper, not on the button. Button's press
    // effect sets translate-y-px, and a Tailwind translate utility replaces
    // the whole transform — so a pressed button lost its -translate-y-1/2 and
    // dropped by half its own height, animated by transition-all. That is the
    // arrow that jumped; it also moved out from under the cursor, which ate
    // the next click.
    <span
      className={`absolute top-1/2 z-10 -translate-y-1/2 ${
        isPrev ? "left-4" : "right-4"
      }`}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={(event) => {
          event.stopPropagation()
          if (isPrev) scrollPrev()
          else scrollNext()
        }}
        className="size-10 rounded-full bg-black/25 text-white/70 backdrop-blur-sm hover:bg-black/40 hover:text-white"
        aria-label={isPrev ? "Previous image" : "Next image"}
      >
        {isPrev ? <ChevronLeft className="size-5" /> : <ChevronRight className="size-5" />}
      </Button>
    </span>
  )
}

interface HotelFullscreenGalleryProps {
  images: string[]
  hotelId: string
  hotelName: string | null
  startIndex: number
  onIndexChange: (index: number) => void
}

/**
 * Full-screen gallery shown in a Dialog. Uses Carousel for the main image
 * with a thumbnail strip below that stays in sync via the Embla API.
 */
function HotelFullscreenGallery({
  images,
  hotelId,
  hotelName,
  startIndex,
  onIndexChange,
}: HotelFullscreenGalleryProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [activeIndex, setActiveIndex] = useState(startIndex)

  // The photo the viewer opened on, fixed for as long as the gallery is open.
  // It used to be read from the prop on every render, and because the prop
  // follows the index back up to the card, picking a thumbnail changed it.
  // Embla treats a changed startIndex as new options and re-initialises, which
  // threw away the move that was in flight — so a thumbnail did nothing until
  // it was clicked again.
  const openedAt = useRef(startIndex)
  const options = useMemo(
    () => ({ loop: images.length > 1, startIndex: openedAt.current }),
    [images.length]
  )

  useEffect(() => {
    if (!api) return
    const handleSelect = () => {
      const next = api.selectedScrollSnap()
      setActiveIndex(next)
      onIndexChange(next)
    }
    api.on("select", handleSelect)
    return () => {
      api.off("select", handleSelect)
    }
  }, [api, onIndexChange])

  return (
    <>
      <Carousel
        setApi={setApi}
        opts={options}
        /*
         * [&>div]: the viewport Carousel puts between this and the slides is
         * part of the shared component and takes no class of its own, so it
         * sized itself to the tallest photo — a 1400px slide inside a 590px
         * box. Giving it the full height here is what keeps every photo
         * inside the screen.
         */
        className="relative min-h-0 flex-1 [&>div]:h-full"
      >
        <CarouselContent className="ml-0 h-full">
          {images.map((src, idx) => (
            <CarouselItem
              key={`${hotelId}-fs-${idx}`}
              className="flex h-full items-center justify-center pl-0"
            >
              {/*
                max-* rather than h-full w-full: a photo smaller than the
                screen used to be blown up to fill it, which is what made the
                gallery look stretched and rough. It now shows at its own size
                and only shrinks when it is bigger than the space.
              */}
              <img
                src={src}
                alt={`${hotelName ?? "Hotel"} photo ${idx + 1}`}
                className="max-h-full max-w-full object-contain"
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {images.length > 1 && (
          <>
            <FullscreenNavButton direction="prev" />
            <FullscreenNavButton direction="next" />
          </>
        )}
      </Carousel>

      {images.length > 1 && (
        <div className="mt-3 flex shrink-0 justify-center gap-2 overflow-x-auto pb-1">
          {images.map((image, idx) => (
            <Button
              key={`${hotelId}-thumb-${idx}`}
              type="button"
              variant="ghost"
              onClick={() => api?.scrollTo(idx)}
              className={`h-auto overflow-hidden rounded-lg border p-0 transition-all ${
                idx === activeIndex ? "border-primary" : "border-border/40"
              }`}
            >
              <img
                src={image}
                alt={`${hotelName ?? "Hotel"} thumbnail ${idx + 1}`}
                className="h-16 w-24 object-cover"
              />
            </Button>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * Centred on the carousel, which is a box that cannot move: the dialog is
 * pinned to the whole window and no longer scrolls inside itself, and the
 * thumbnail strip below it keeps its height.
 *
 * The centring sits on the wrapper for the same reason as in
 * {@link GalleryNavButton} — Button's press effect would otherwise replace it.
 */
function FullscreenNavButton({ direction }: { direction: "prev" | "next" }) {
  const { scrollPrev, scrollNext } = useCarousel()
  const isPrev = direction === "prev"

  return (
    <span
      className={`absolute top-1/2 z-10 -translate-y-1/2 ${
        isPrev ? "left-3" : "right-3"
      }`}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => (isPrev ? scrollPrev() : scrollNext())}
        className="size-11 rounded-full bg-black/40 text-white/80 backdrop-blur-sm hover:bg-black/60 hover:text-white"
        aria-label={isPrev ? "Previous image" : "Next image"}
      >
        {isPrev ? <ChevronLeft className="size-6" /> : <ChevronRight className="size-6" />}
      </Button>
    </span>
  )
}

function MetaPill({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-transparent px-3 py-1.5 text-xs font-medium text-foreground/80"
    >
      {children}
    </span>
  )
}
